import type {
	AdminSettingsHomeGenericSuccessResponse,
	BrandingResponse,
	FunctionsResponse,
	PatchBrandingRequest,
	PatchFunctionsRequest,
	PatchSettingsRequest,
	SeatUsageResponse,
	SettingsResponse,
} from "@performyard/service-contracts";

import { queryClient } from "../../../queryClient";
import { MeResponse } from "../auth";
import request, { getUrl } from "./internal-base-request";

const BASE = "/v1/admin-settings/home";

const withQuery = (path: string, companyId?: string) =>
	companyId ? `${path}?companyId=${encodeURIComponent(companyId)}` : path;

export const getAdminSettingsHomeSeatUsage = async (companyId?: string): Promise<SeatUsageResponse> => {
	const [data] = await request<SeatUsageResponse>(withQuery(`${BASE}/seat-usage`, companyId));
	return data;
};

export const getAdminSettingsHomeBranding = async (companyId?: string): Promise<BrandingResponse> => {
	const [data] = await request<BrandingResponse>(withQuery(`${BASE}/branding`, companyId));
	return data;
};

export const patchAdminSettingsHomeBranding = async (
	body: PatchBrandingRequest,
	companyId?: string
): Promise<AdminSettingsHomeGenericSuccessResponse> => {
	const [data] = await request<AdminSettingsHomeGenericSuccessResponse>(withQuery(`${BASE}/branding`, companyId), {
		method: "PATCH",
		body: JSON.stringify(body),
	});
	return data;
};

// Uses raw fetch (not the shared `request` helper) because that helper sets a JSON Content-Type that breaks multipart uploads.
export const postAdminSettingsHomeLogo = async (
	file: File,
	companyId?: string
): Promise<AdminSettingsHomeGenericSuccessResponse> => {
	const formData = new FormData();
	formData.append("image", file);
	const currentUser = queryClient.getQueryData(["auth-current-user"]) as MeResponse;
	const url = getUrl(withQuery(`${BASE}/branding/logo`, companyId));
	const response = await fetch(url, {
		method: "POST",
		headers: {
			"X-CSRFToken": window.xsrf_val,
			Authorization: `Bearer ${currentUser.me.authorization?.token}`,
		},
		body: formData,
	});
	const data = (await response.json()) as AdminSettingsHomeGenericSuccessResponse;
	if (!response.ok) {
		throw new Error(`Logo upload failed (${response.status})`);
	}
	return data;
};

export const postAdminSettingsHomeBrandingReset = async (
	companyId?: string
): Promise<AdminSettingsHomeGenericSuccessResponse> => {
	const [data] = await request<AdminSettingsHomeGenericSuccessResponse>(
		withQuery(`${BASE}/branding/reset`, companyId),
		{ method: "POST" }
	);
	return data;
};

export const getAdminSettingsHomeFunctions = async (companyId?: string): Promise<FunctionsResponse> => {
	const [data] = await request<FunctionsResponse>(withQuery(`${BASE}/functions`, companyId));
	return data;
};

export const patchAdminSettingsHomeFunctions = async (
	body: PatchFunctionsRequest,
	companyId?: string
): Promise<AdminSettingsHomeGenericSuccessResponse> => {
	const [data] = await request<AdminSettingsHomeGenericSuccessResponse>(withQuery(`${BASE}/functions`, companyId), {
		method: "PATCH",
		body: JSON.stringify(body),
	});
	return data;
};

export const getAdminSettingsHomeSettings = async (companyId?: string): Promise<SettingsResponse> => {
	const [data] = await request<SettingsResponse>(withQuery(`${BASE}/settings`, companyId));
	return data;
};

export const patchAdminSettingsHomeSettings = async (
	body: PatchSettingsRequest,
	companyId?: string
): Promise<AdminSettingsHomeGenericSuccessResponse> => {
	const [data] = await request<AdminSettingsHomeGenericSuccessResponse>(withQuery(`${BASE}/settings`, companyId), {
		method: "PATCH",
		body: JSON.stringify(body),
	});
	return data;
};
